"use client";

import { useEffect, useState } from "react";
import type { RecommendationCounts } from "@/lib/data/types";

function toBuckets(counts: RecommendationCounts) {
  const buy = counts.strongBuy + counts.buy;
  const hold = counts.hold;
  const sell = counts.sell + counts.strongSell;
  return { buy, hold, sell, total: buy + hold + sell };
}

/**
 * Grobe Naeherung, keine echte Analysten-Historie: Yahoo liefert nur Bucket-Anzahlen je
 * Monat, keine einzelnen Hoch-/Herabstufungs-Ereignisse. Verschiebungen zwischen den
 * Buy/Hold/Sell-Summen der letzten 30 Tage werden daher als Hoch-/Herabstufungen gelesen.
 */
function trendHint(current: RecommendationCounts, prevMonth: RecommendationCounts): string | undefined {
  const now = toBuckets(current);
  const prev = toBuckets(prevMonth);
  const upgrades = Math.max(0, now.buy - prev.buy) + Math.max(0, prev.sell - now.sell);
  const downgrades = Math.max(0, prev.buy - now.buy) + Math.max(0, now.sell - prev.sell);
  if (upgrades === 0 && downgrades === 0) return undefined;

  const parts: string[] = [];
  if (upgrades > 0) parts.push(`${upgrades} Hochstufung${upgrades === 1 ? "" : "en"}`);
  if (downgrades > 0) parts.push(`${downgrades} Herabstufung${downgrades === 1 ? "" : "en"}`);
  return `${parts.join(" · ")} in 30 Tagen`;
}

export function AnalystConsensusBars({
  current,
  prevMonth,
}: {
  current: RecommendationCounts;
  prevMonth?: RecommendationCounts;
}) {
  const [filled, setFilled] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setFilled(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const { buy, hold, sell, total } = toBuckets(current);
  if (total === 0) return null;

  const widthOf = (n: number) => (filled ? `${(n / total) * 100}%` : "0%");
  const hint = prevMonth ? trendHint(current, prevMonth) : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex h-5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full bg-positive transition-[width] duration-700 ease-out"
          style={{ width: widthOf(buy) }}
        />
        <div
          className="h-full bg-muted-foreground/40 transition-[width] duration-700 ease-out delay-100"
          style={{ width: widthOf(hold) }}
        />
        <div
          className="h-full bg-negative transition-[width] duration-700 ease-out delay-200"
          style={{ width: widthOf(sell) }}
        />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>Kaufen {buy}</span>
        <span>Halten {hold}</span>
        <span>Verkaufen {sell}</span>
      </div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}
